import { CONFIG_OPTION_TOAST_ERROR } from 'common/toast';
import DropdownOption from 'components/Common/DropdownOption';
import LabelWrapper from 'components/Common/LabelWrapper';
import { useFormik } from 'formik';
import { AI_MODEL_BOOSTING_NEWS_TRAVEL_OPTIONS, SYSTEM_CONFIG_KEY } from 'helpers/constans';
import SpinnerScreen from 'pages/News/Components/SpinnerScreen';
import { formatHighlightTextTravel } from 'pages/News/functions';
import useNotificationAudio from 'pages/News/GenerateNews/useNotification';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { CardBody, Col, Input, Modal, ModalBody, ModalHeader, Row, Spinner } from 'reactstrap';
import { getConfiguration, getDetailSimulatorGenerateNewsTravel, postSimulatorGenerateNewsTravel } from 'store/thunks';
import * as Yup from "yup";

interface ModalSimulatorNewsTravelProps {
    isOpen: boolean;
    sourcesIds: any[];
    onRefresh?: () => void,
    onClose?: () => void,
}

const ModalSimulatorNewsTravel: React.FC<ModalSimulatorNewsTravelProps> = ({
    isOpen, onRefresh, onClose, sourcesIds = []
}) => {
    const { t } = useTranslation();
    const { playNotification } = useNotificationAudio();
    const AI_MODEL_NEWS_TRAVEL_OPTIONS_LANG = AI_MODEL_BOOSTING_NEWS_TRAVEL_OPTIONS.map((item: any) => ({ value: item?.value, label: t(item?.label) }));

    const NEWS_V2_LOG_STATUS: any = {
        INITIAL: 1,
        ERROR: 2,
        COMPLETED: 3,
    };
    const isIntervalRef = useRef<boolean>(false);
    let intervalId: any;

    const [isGenerating, setIsGenerating] = useState<boolean>(false);

    const [newsDetail, setNewsDetail] = useState<any>({});

    const handleClose = () => {
        onClose && onClose();
        formik.setValues({
            ai_model: AI_MODEL_NEWS_TRAVEL_OPTIONS_LANG[0],
            prompt: '',
            title: '',
        });
        formik.setErrors({});
        setNewsDetail((_prev: any) => null);
    }

    const getDetailNewsTravel = async (id: string | number) => {
        try {
            if (isIntervalRef.current === false && !!intervalId) {
                clearInterval(intervalId);
                return;
            }
            const response: any = await getDetailSimulatorGenerateNewsTravel(id);
            if (response?.code === 200) {
                if ([
                    NEWS_V2_LOG_STATUS?.COMPLETED,
                    NEWS_V2_LOG_STATUS.ERROR
                ]?.includes(response?.data?.status)) {
                    isIntervalRef.current = false;
                    setIsGenerating((_prev) => false);
                    setNewsDetail((_prev: any) => (response?.data || null));
                    clearInterval(intervalId);
                    playNotification();
                }
            }
        } catch (error: any) {
            isIntervalRef.current = false;
            setIsGenerating((_prev) => false);
            setNewsDetail((_prev: any) => null);
            return error;
        }
    };

    // Update: correct according to available thunk
    const handleSubmit = async (values: any) => {
        try {
            if (isGenerating) return;
            setIsGenerating(true);
            setNewsDetail((_prev: any) => null);
            const request = {
                ai_model: values?.ai_model?.value || '',
                prompt: values?.prompt || '',
                ids: (sourcesIds || [])?.map((item: any) => item?.value),
                title: values?.title || ''
            };
            const response: any = await postSimulatorGenerateNewsTravel(request);
            if (response?.code === 200 && response?.data?.id) {
                isIntervalRef.current = true;
                intervalId = setInterval(() => {
                    getDetailNewsTravel(response?.data?.id);
                }, 20000);
                getDetailNewsTravel(response?.data?.id);
            } else {
                isIntervalRef.current = false;
                setIsGenerating((_prev) => false);
                toast(`${response || ""}`, CONFIG_OPTION_TOAST_ERROR);
            }

        } catch (error: any) {
            setIsGenerating(false);
            toast(`${error?.message || ''}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    };

    const validationSchema = Yup.object({
        prompt: Yup.string().required(`${t("This is required")}`),
        title: Yup.string().nullable(),
    });

    const formik = useFormik({
        initialValues: {
            ai_model: AI_MODEL_NEWS_TRAVEL_OPTIONS_LANG[0],
            prompt: '',
            title: '',
        },
        validationSchema: validationSchema,
        onSubmit: handleSubmit,
        enableReinitialize: true,
    });

    const handleCallAllOption = async () => {
        try {
            const [res1]: any = await Promise.all([getConfiguration()]);
            const _configs = res1?.data?.list || [];
            const _ai_model = AI_MODEL_NEWS_TRAVEL_OPTIONS_LANG[0];
            const _generate_prompt = _configs?.find((item: any) => item?.key === SYSTEM_CONFIG_KEY.SIMULATOR_NEWS_TRAVEL_GENERATE_PROMPT)?.value || '';
            formik.setFieldValue('prompt', _generate_prompt);
            formik.setFieldValue('ai_model', _ai_model);
        } catch (error) {
            return error;
        }
    };

    useEffect(() => {
        if (isOpen) {
            handleCallAllOption();
        }
    }, [isOpen]);

    return (
        <>
            <Modal
                isOpen={isOpen}
                centered={true}
                size="xl"
                scrollable={false}
                className="modal-fullscreen"
            >
                <ModalHeader toggle={handleClose}>
                    <>{t("Generate Travel News (Simulator)")}</>
                </ModalHeader>
                <ModalBody className="pt-2">
                    <form onSubmit={formik.handleSubmit}>
                        <div>
                            <div className="card-body mt-0 pb-1">
                                <Row>
                                    <Col sm={12} md={12} className='mt-3 mt-md-0 mb-3'>
                                        <LabelWrapper label={t('Generate Prompt')} isShow={true}>
                                            <Input
                                                name="prompt"
                                                type="textarea"
                                                className="form-control mb-3 mb-lg-2 mb-xxl-1"
                                                placeholder=""
                                                style={{ height: '200px' }}
                                                value={formik?.values?.prompt}
                                                onChange={formik.handleChange}
                                                autoComplete="off"
                                                disabled={isGenerating}
                                            />
                                            <div className="w-100 mb-1 mb-xl-0 fs-12 text-muted pt-0">
                                                {t(`{{keywords}} will replace dynamically`, { keywords: `{chat_history_block}, {search_query}, {current_date}, {weather_info}, {places_data_json}` })}
                                            </div>
                                            {formik.errors.prompt && formik.touched.prompt && (
                                                <div className="text-danger">{formik.errors.prompt}</div>
                                            )}
                                        </LabelWrapper>
                                    </Col>
                                    <Col sm={12} md={8} lg={9} xl={9} xxl={9} className='mt-3 mt-md-2'>
                                        <div className='w-100 d-flex justify-content-between align-items-center gap-4'>
                                            <div>
                                                <LabelWrapper label={t('AI Model')} isShow={true}>
                                                    <DropdownOption
                                                        name="ai_model"
                                                        dataList={AI_MODEL_NEWS_TRAVEL_OPTIONS_LANG || []}
                                                        placeholder={`${t("AI Model")}...`}
                                                        className={`dropdown-status-rounded z-hight mb-3 mb-lg-2 mb-xxl-1`}
                                                        classNamePrefix="name-prefix"
                                                        style={{ width: '150px' }}
                                                        initialValue={formik?.values?.ai_model}
                                                        onChangeSelect={(e: any) => {
                                                            formik.setFieldValue('ai_model', e);
                                                        }}
                                                        disabled={isGenerating}
                                                    />
                                                </LabelWrapper>
                                            </div>
                                            <div className='w-100'>
                                                <LabelWrapper label={t('Title') + ` (${t("Keyword")})`} isRequired={false} isShow={true}>
                                                    <Input
                                                        name="title"
                                                        type="text"
                                                        className={`form-control mb-3 mb-lg-2 mb-xxl-1`}
                                                        placeholder=""
                                                        value={formik?.values?.title}
                                                        style={{ width: '100%', minWidth: '100px' }}
                                                        onChange={formik.handleChange}
                                                        autoComplete="off"
                                                        disabled={isGenerating}
                                                    />
                                                    {formik.errors.title && formik.touched.title && (
                                                        <div className="text-danger">{formik.errors.title}</div>
                                                    )}
                                                </LabelWrapper>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col sm={12} md={4} lg={3} xl={3} xxl={3} className='mt-0 mt-lg-2'>
                                        <div className='w-100 d-flex align-items-center gap-4'>
                                            <button
                                                className="btn btn-primary fs-14 px-1 d-flex justify-content-center align-item-center mb-3 mb-lg-2 mb-xxl-1"
                                                type="submit"
                                                disabled={isGenerating}
                                                style={{ width: '160px', minWidth: '160px' }}
                                            >
                                                {isGenerating ? <span style={{ transform: 'translateY(1px)' }}><Spinner size="sm" /></span> : <i className="ri-refresh-line" style={{ lineHeight: '22px' }} />}
                                                <span className="ms-1">{t("Generate News")}</span>
                                            </button>
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col sm={12} md={12} className='mt-1 '>
                                        <span className="text-muted fs-13">{t('Place List')}:</span> {(sourcesIds || [])?.map((item: any) => item?.label)?.join(', ')}
                                    </Col>
                                </Row>
                            </div>

                            <CardBody className="pt-3">
                                <Row className="h-100-pc">
                                    <Col lg={6} className="mt-2 mt-md-0 mb-3 position-relative">
                                        <span className="position-absolute text-success fs-13 text-end px-2" style={{
                                            top: 0,
                                            left: 20,
                                            transform: `translateY(-10px)`,
                                            zIndex: 10,
                                            backgroundColor: 'var(--vz-secondary-bg)'
                                        }}>
                                            {t('News')}
                                        </span>
                                        <div className="inner-modal-body color-scrollbar-os position-relative " style={{ border: '1.5px dashed var(--vz-success)', minHeight: '65dvh', maxHeight: '65dvh', overflowY: "auto" }}>
                                            <div className="p-2">
                                                {!!newsDetail?.title ? <h5 className="mb-3 fs-20 mt-3" dangerouslySetInnerHTML={{ __html: newsDetail?.title ?? "" }}></h5> : null}
                                                {!!newsDetail?.body ? <div className="mb-3 fs-14" style={{ whiteSpace: 'pre-line', lineHeight: '170%' }} dangerouslySetInnerHTML={{ __html: newsDetail?.body ?? "" }}>
                                                </div> : null}
                                            </div>
                                            {isGenerating && <SpinnerScreen zIndex={9} isShow={true} />}
                                        </div>
                                    </Col>
                                    <Col lg={6} className="mt-2 mt-md-0 mb-3 position-relative">
                                        <span className="position-absolute text-success fs-13 text-end px-2" style={{
                                            top: 0,
                                            left: 20,
                                            transform: `translateY(-10px)`,
                                            zIndex: 10,
                                            backgroundColor: 'var(--vz-secondary-bg)'
                                        }}>
                                            {t('Prompt')}
                                        </span>
                                        <div className="inner-modal-body color-scrollbar-os position-relative" style={{ border: '1.5px dashed var(--vz-success)', minHeight: '65dvh', maxHeight: '65dvh', overflowY: "auto" }}>
                                            <div className="p-2">
                                                <div className="mb-3 fs-14" style={{ whiteSpace: 'pre-line', lineHeight: '170%' }} dangerouslySetInnerHTML={{ __html: formatHighlightTextTravel(newsDetail?.prompt ?? "") }}>

                                                </div>
                                            </div>
                                            {isGenerating && <SpinnerScreen zIndex={9} isShow={true} />}
                                        </div>
                                    </Col>
                                </Row>
                            </CardBody>
                        </div>
                    </form>
                </ModalBody>
            </Modal>
        </>
    );
};

export default React.memo(ModalSimulatorNewsTravel);

